import { Link, Stack, Text, styled } from '@ltvco/refresh-lib/theme';
import {
  AccessTime,
  InsertDriveFileOutlined,
  SearchOutlined,
} from '@mui/icons-material';
import { useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { theme } from 'theme';

const MoreWaysItem = styled(Stack)(({ theme: muiTheme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: muiTheme.spacing(1.5),
  padding: muiTheme.spacing(1.5, 2),
  borderRadius: '32px',
  backgroundColor: muiTheme.palette.action.hover,
  width: '100%',
}));

export type MoreWaysSectionItem = {
  /** Optional stable key; falls back to index when mapping. */
  id?: string;
  icon: ReactNode;
  children: ReactNode;
};

function useDefaultMoreWaysItems(): MoreWaysSectionItem[] {
  const navigate = useNavigate();

  return useMemo(
    () => [
      {
        id: 'recent-reports',
        icon: (
          <AccessTime
            fontSize="small"
            color="action"
            sx={{ color: theme.palette.primary.main }}
          />
        ),
        children: (
          <Text variant="body2">
            Add from your{' '}
            <Link
              component="button"
              onClick={() => navigate('/chatbv/chat/library/Recent%20Reports')}
              sx={{ verticalAlign: 'baseline' }}
            >
              recent reports.
            </Link>
          </Text>
        ),
      },
      {
        id: 'new-report',
        icon: (
          <InsertDriveFileOutlined
            fontSize="small"
            sx={{ color: theme.palette.primary.main }}
          />
        ),
        children: (
          <Text variant="body2">
            Directly from a new{' '}
            <Link
              component="button"
              onClick={() => navigate('/chatbv/chat')}
              sx={{ verticalAlign: 'baseline' }}
            >
              report.
            </Link>
          </Text>
        ),
      },
      {
        id: 'new-search',
        icon: (
          <SearchOutlined
            fontSize="small"
            color="action"
            sx={{ color: theme.palette.primary.main }}
          />
        ),
        children: (
          <Text variant="body2">
            Automatically create a build from a new{' '}
            <Link
              component="button"
              onClick={() => navigate('/chatbv/chat')}
              sx={{ verticalAlign: 'baseline' }}
            >
              search
            </Link>
          </Text>
        ),
      },
    ],
    [navigate]
  );
}

export type MoreWaysSectionProps = {
  /** Custom rows; when omitted or empty, the default three items are shown. */
  items?: MoreWaysSectionItem[];
  /** Same as `items` — use whichever reads clearer at the call site. */
  moreWaysArray?: MoreWaysSectionItem[];
};

export const MoreWaysSection = ({
  items,
  moreWaysArray,
}: MoreWaysSectionProps) => {
  const defaultItems = useDefaultMoreWaysItems();
  const fromProps = items ?? moreWaysArray;
  const resolvedItems =
    fromProps !== undefined && fromProps.length > 0 ? fromProps : defaultItems;

  return (
    <Stack spacing={1.5} width="100%" alignItems="start">
      <Text variant="body2" color="text.secondary">
        More ways to add saved reports
      </Text>
      {resolvedItems.map((item, index) => (
        <MoreWaysItem key={item.id ?? index}>
          {item.icon}
          {item.children}
        </MoreWaysItem>
      ))}
    </Stack>
  );
};
